import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import styles from './SafetyVersePage.module.css';
import { ISafetyPost } from '../../types/types';
import config from '../../config/config';
import IndividualCard from '../../components/IndividualCard/IndividualCard';
import ContactUs from '../../components/ContactUs/ContactUs';

const SafetyVersePage: React.FC = () => {
  const [safetyPosts, setSafetyPosts] = useState<ISafetyPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to extract text from the first block of verses portable text
  const getVerseSummary = (portableTextBlocks: any): string => {
    if (typeof portableTextBlocks === 'string') {
      return portableTextBlocks;
    }
    
    if (!portableTextBlocks || !Array.isArray(portableTextBlocks) || portableTextBlocks.length === 0) {
      return 'No verse text available.';
    }
    
    const firstBlock = portableTextBlocks[0];
    if (firstBlock && firstBlock.children && Array.isArray(firstBlock.children) && firstBlock.children.length > 0) {
      return firstBlock.children
        .filter((child: any) => child.text)
        .map((child: any) => child.text)
        .join(' ');
    }
    
    return 'Verse text not in expected format.';
  };

  useEffect(() => {
    const fetchSafetyPosts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${config.API_BASE_URL}/safety-posts`);
        
        if (response.data && response.data.length > 0) {
          // Sort fetched posts by date
          const sortedPosts = response.data.sort((a: any, b: any) => 
            new Date(b.date).getTime() - new Date(a.date).getTime()
          );
          
          setSafetyPosts(sortedPosts);
          setError(null);
        } else {
          setError('No safety posts found.');
          setSafetyPosts([]);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching safety posts:', err);
        setError('Error loading safety posts. Please try again later.');
        setSafetyPosts([]);
        setLoading(false);
      }
    };

    fetchSafetyPosts();
  }, []);

  if (loading) return <div className={styles.loading}>Loading safety posts...</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (safetyPosts.length === 0) return <div className={styles.error}>No safety posts found.</div>;

  return (
    <div className={styles.container}>
      <section className={styles.safetyVerseIntro}>
        <h1 className={styles.pageTitle}>Safety Team Bible Verses</h1>
        <p className={styles.pageSubtitle}>
          Biblical guidance and encouragement for church safety team members
        </p>
      </section>

      <section className={styles.safetyVersesList}>
        <h2>Key Bible Verses For Church Safety Teams</h2>
        <p className={styles.clickInstruction}>Click on any verse to view its detailed teaching</p>
        <div className={styles.versesContainer}>
          {safetyPosts.map((post) => (
            <Link 
              to={`/safety-verses/${post.id}`}
              key={post.id}
              className={styles.verseLink}
            >
              <div className={`${styles.verseCard} ${styles.clickable}`}>
                <IndividualCard
                  reference={post.reference}
                  verses={getVerseSummary(post.verses)}
                  date={post.date}
                />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <ContactUs />
    </div>
  );
};

export default SafetyVersePage;
